/// <reference types="vitest" />
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  GoogleAuthProvider,
  IdTokenResult,
  NextOrObserver,
  onIdTokenChanged,
  signInWithPopup,
  signOut as firebaseSignOut,
  Unsubscribe,
  User,
  UserCredential,
} from "firebase/auth";
import {
  doc,
  DocumentData,
  DocumentSnapshot,
  onSnapshot,
  Unsubscribe as FirestoreUnsubscribe,
} from "firebase/firestore";

import { auth } from "integrations/firebase/auth";
import { db } from "integrations/firebase/firestore";
import { logError } from "shared/services/ErrorReporting";
import { t } from "i18next";
import { setImpersonationActive } from "@/hooks/impersonationState";
import { requestImpersonationStop } from "@/hooks/impersonationStop";
import { signInRefusalOf } from "@/shared/signInRefusal";
import { toast } from "@/utils/toast";

/**
 * Every role the app can hold, in the app's own lowercase spelling.
 *
 * A runtime list and not only a type, because a role arriving from a claim or
 * a user record has to be *checked* against it — see `normalizeRole`. It
 * mirrors `UserRole` in `enums.py`, and every role in it is one a screen routes
 * on (Jira NAV-113).
 */
export const USER_ROLES = [
  "end_user",
  "bu_admin",
  "super_admin",
  "group_legal",
] as const;

export type UserRole = (typeof USER_ROLES)[number];

export interface UserProfile {
  uid: string;
  email: string | null;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  photoUrl?: string;
  role?: UserRole;
  businessUnitId?: string;
  /** The house inside that unit this person belongs to (ADR 0025); absent
   *  is the unit itself. */
  houseId?: string | null;
  country?: string | null;
  managerId?: string | null;
  onboardingCompleted?: boolean;
  /** How far through the walkthrough they got, so returning resumes there
   *  rather than starting again (Jira NAV-26). Absent on records written
   *  before it was kept, which reads as the first screen. */
  onboardingStep?: number;
  status?: "ACTIVE" | "PENDING" | "INACTIVE";
  createdAt?: unknown;
  updatedAt?: unknown;
}

export interface ImpersonationClaim {
  uid: string;
  /** The target's role as the backend writes it — the same UPPERCASE enum the
   *  top-level `role` claim carries, so it goes through `normalizeRole` too. */
  role?: string;
  business_unit_id?: string;
  /** The target's house, so acting as a house's member shows that house's
   *  catalogue and admin surfaces (ADR 0025). */
  house_id?: string;
  /** When the session started, in seconds. Honoured only while it is newer
   *  than the token's own sign-in (`auth_time`) — see `isImpersonationActive`. */
  started_at?: number;
  expires_at: number;
}

interface IdTokenClaims {
  role?: UserRole;
  business_unit_id?: string;
  house_id?: string;
  impersonating?: ImpersonationClaim;
}

export interface IAuthContext {
  user: User | null;
  profile: UserProfile | null;
  /** The acting identity's role: the impersonation target's while a session is
   *  active, otherwise the signed-in user's own. Drives what the app shows —
   *  nav, role gates, BU-scoped views. */
  role: UserRole | null;
  /** The acting identity's business unit, on the same rule as `role`. */
  businessUnitId: string | null;
  /** The acting identity's house inside that unit (ADR 0025), on the same
   *  rule — and `null` for the unit itself, which is every member of a
   *  tenant with no houses. For a BU admin this is what scopes them: one
   *  who belongs to a house administers that house and nothing else. */
  houseId: string | null;
  /** Whose records the app should show: the impersonation target's uid while
   *  a session is active, otherwise the signed-in user's own.
   *
   *  The one documented way to key an identity-scoped *read* — "my tools",
   *  "my requests". Reading `user.uid` directly in a view is what made
   *  impersonation show the admin's own records under someone else's name.
   *  Writes are a different question and do not belong here: `user` stays the
   *  admin, so every write is theirs, and `useImpersonationGuard` refuses
   *  them for the duration rather than misattributing them. */
  actingUid: string | null;
  isImpersonating: boolean;
  /** The signed-in user's own role, never the target's.
   *
   *  `firestore.rules` reads the top-level `role`/`business_unit_id` claims and
   *  ignores the `impersonating` one, so the database still answers to *these*
   *  values throughout a session — impersonation is a view, not a privilege
   *  drop (PRD H5). Anything deciding whether
   *  a write will be *permitted* has to ask these, not the pair above, or the
   *  UI offers an action the rules then refuse. */
  realRole: UserRole | null;
  /** The signed-in user's own business unit, on the same rule as `realRole`. */
  realBusinessUnitId: string | null;
  /** The signed-in user's own house, on the same rule as `realRole`. */
  realHouseId: string | null;
  impersonatedUser: UserProfile | null;
  realUser: UserProfile | null;
  onboardingCompleted: boolean;
  /** True once the current user's profile doc has produced its first
   *  snapshot (or there is no user). Until then onboardingCompleted is the
   *  `?? false` fallback and must not drive navigation. */
  profileResolved: boolean;
  /**
   * True when the profile subscription *failed* rather than answering.
   *
   * Kept apart from `profileResolved`, which the error path also sets, because
   * a decision taken on the absence it leaves behind is a decision taken on no
   * evidence. `roleMissing` below already refuses to decide without this, and
   * the root gate's walkthrough branch needs the same guard: a snapshot error
   * makes `onboardingCompleted` fall back to false, which reads as a reader
   * who has never been introduced.
   */
  profileReadFailed: boolean;
  /** True when the token's role/BU claims disagree with the profile doc and a
   *  force-refresh has already failed to reconcile them — the session can only
   *  be fixed by signing in again. See the staleness check in the id-token
   *  handler. */
  claimsStale: boolean;
  /** True once the app has waited as long as it usefully can for a role and
   *  none has arrived (NAV-101).
   *
   *  **Not the same question as `role === null`**, which is also the ordinary
   *  first moment of every session — and treating the two as one is the bug
   *  this exists to remove: the root gate held a signed-in visitor on the
   *  sign-in page "until the role resolves", with no branch for a role that
   *  never would, so a role-less account waited for ever under a working menu.
   *
   *  Bounded two ways, because there are two ways to have no role and only one
   *  of them is worth waiting on. When the person's own record carries no role
   *  either, nothing is in flight and the answer is immediate. When the record
   *  *does* carry one the token has not caught up with, the claim-refresh path
   *  above is mid-flight and is given its window first — that is the "merely
   *  slow" session, and it must land where it was going. */
  roleMissing: boolean;
  loading: boolean;
  error?: Error;
  isLoggedIn: boolean;

  signInWithGoogle: () => Promise<UserCredential | void>;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<IAuthContext>({
  user: null,
  profile: null,
  role: null,
  businessUnitId: null,
  houseId: null,
  actingUid: null,
  isImpersonating: false,
  realRole: null,
  realBusinessUnitId: null,
  realHouseId: null,
  impersonatedUser: null,
  realUser: null,
  onboardingCompleted: false,
  profileResolved: false,
  profileReadFailed: false,
  claimsStale: false,
  roleMissing: false,
  loading: true,
  isLoggedIn: false,
  signInWithGoogle: async () => {},
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

const GOOGLE_PROVIDER = new GoogleAuthProvider();

// How many token refreshes one claim mismatch is worth before the session is
// declared unrecoverable, and how long the second waits. Two rather than one
// because the backend writes the user doc before the claim (see
// ExecuteRoleChangeCommandUseCase), so the doc snapshot that triggers the first
// refresh can beat the claim write; the delay is what lets it land.
const CLAIM_REFRESH_ATTEMPTS = 2;
const CLAIM_REFRESH_RETRY_MS = 3000;

// How long a token with no role is given to grow one before the app says so
// (NAV-101) — and only in the case where something is actually in flight: the
// person's own record names a role the token has not caught up with, so the
// refresh path above is working on it. Long enough for both attempts (the
// second is itself delayed by CLAIM_REFRESH_RETRY_MS) plus their round trips.
// The other case — no role anywhere — waits for nothing at all, because
// nothing is coming.
const ROLE_GRACE_MS = CLAIM_REFRESH_ATTEMPTS * CLAIM_REFRESH_RETRY_MS + 4000;

const KNOWN_ROLES: ReadonlySet<string> = new Set(USER_ROLES);

/**
 * The role a claim or a user record carries, or undefined if it carries none.
 *
 * The backend stores roles as UPPERCASE enum values (e.g. "SUPER_ADMIN") in
 * custom claims and Firestore; the app compares lowercase `UserRole` literals.
 * This is the single point roles enter the app, so it is where they are
 * lowercased — and, since Jira NAV-113, where they are *checked*.
 *
 * Casting whatever string arrives would make every unusable value a role:
 * `""` and a name no version of this app ever wrote both come out
 * truthy-or-empty, and neither reads as absent. That is the state the
 * no-role page exists for (NAV-101) and the one it could not be reached from,
 * because `roleMissing` asks whether a role is missing and was being told one
 * was there. Anything outside the vocabulary is now the same answer as nothing
 * at all, which is the honest one: we cannot tell what this person is.
 */
const normalizeRole = (value: unknown): UserRole | undefined => {
  if (typeof value !== "string") return undefined;
  const lowered = value.toLowerCase();
  return KNOWN_ROLES.has(lowered) ? (lowered as UserRole) : undefined;
};

const readClaims = (
  tokenResult: IdTokenResult,
): IdTokenClaims => {
  const claims = (tokenResult.claims ?? {}) as Record<string, unknown>;
  return {
    role: normalizeRole(claims.role),
    business_unit_id: (claims.business_unit_id as string) ?? undefined,
    house_id: (claims.house_id as string) ?? undefined,
    impersonating: (claims.impersonating as ImpersonationClaim) ?? undefined,
  };
};

const snapshotToProfile = (
  snapshot: DocumentSnapshot<DocumentData>,
): UserProfile | null => {
  if (!snapshot.exists()) {
    return null;
  }
  const data = snapshot.data() ?? {};
  return { uid: snapshot.id, ...data } as UserProfile;
};

/** How long signing out waits for the impersonation stop to be filed. */
const SIGN_OUT_STOP_WAIT_MS = 3000;

/** When this token's sign-in happened, in seconds — the `auth_time` claim,
 *  which a refresh carries forward unchanged and only a new sign-in moves. */
const signInTimeOf = (tokenResult: IdTokenResult): number => {
  const claimed = (tokenResult.claims as Record<string, unknown>)?.auth_time;
  if (typeof claimed === "number") return claimed;
  return Date.parse(tokenResult.authTime) / 1000;
};

/**
 * Is this impersonation claim a session of *this* sign-in, still running?
 *
 * **The start has to be newer than the sign-in** (Jira NAV-130). The claim is
 * persisted on the admin's own account, so it outlives a sign-out, and a
 * session belongs to the sign-in that started it: one started during this
 * sign-in is newer than its `auth_time`; one left from an earlier sign-in is
 * older, and so is a claim with no start at all. Stale claims are cleared in
 * the background by the handler below.
 *
 * **That includes a sign-in on another device.** It sees the running
 * session's claim as older than its own sign-in and asks for it to stop, so
 * the session ends on the first device too. Intended: an impersonation is one
 * admin, one view, and signing in elsewhere is an unambiguous way to leave it.
 */

const isImpersonationActive = (
  claim: ImpersonationClaim | undefined,
  signInTime: number,
): claim is ImpersonationClaim => {
  if (!claim) return false;
  if (typeof claim.expires_at !== "number") return false;
  if (typeof claim.started_at !== "number") return false;
  if (!(claim.started_at >= signInTime)) return false;
  return claim.expires_at * 1000 > Date.now();
};

/**
 * Whether the signed-in account's current token carries a session this
 * provider will honour — the same test the token handler below applies before
 * it turns `isImpersonating` on. A start that the backend confirmed can still
 * be refused here (a sign-in older than the session's start), and a caller
 * waiting for the banner has to learn that rather than wait for ever (Jira
 * NAV-229).
 */
export async function isImpersonationHonoured(
  firebaseUser: User | null | undefined,
): Promise<boolean> {
  if (!firebaseUser) return false;
  const tokenResult = await firebaseUser.getIdTokenResult();
  return isImpersonationActive(
    readClaims(tokenResult).impersonating,
    signInTimeOf(tokenResult),
  );
}

export const AuthContextProvider = ({ children }: PropsWithChildren) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [realUser, setRealUser] = useState<UserProfile | null>(null);
  const [role, setRole] = useState<UserRole | null>(null);
  const [businessUnitId, setBusinessUnitId] = useState<string | null>(null);
  const [houseId, setHouseId] = useState<string | null>(null);
  const [realRole, setRealRole] = useState<UserRole | null>(null);
  const [realBusinessUnitId, setRealBusinessUnitId] = useState<string | null>(
    null,
  );
  const [realHouseId, setRealHouseId] = useState<string | null>(null);
  const [actingUid, setActingUid] = useState<string | null>(null);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [profileResolved, setProfileResolved] = useState(false);
  const [claimsStale, setClaimsStale] = useState(false);
  const [roleGraceElapsed, setRoleGraceElapsed] = useState(false);
  /** True when the profile subscription *failed* rather than answering.
   *
   *  Kept apart from `profileResolved`, which the error path also sets — the
   *  app has to stop loading either way. The difference matters to exactly one
   *  question: a read that failed has told us nothing about whether this
   *  account has a role, and treating its empty result as "no role" is the
   *  same mistake as reading a denied query as an empty list. */
  const [profileReadFailed, setProfileReadFailed] = useState(false);
  // The token we have already force-refreshed away from over a claim mismatch,
  // or null if no refresh is outstanding. Keyed on the token rather than the
  // uid so the three states are distinguishable: no attempt yet (null), an
  // attempt still in flight (this token), and an attempt that landed on a new
  // token which *still* disagrees (a different token) — only the last of which
  // means the session cannot heal itself. Keying on the uid conflated the
  // middle and last cases, so a second profile snapshot arriving before the
  // refresh did would flash the banner.
  const refreshedFromTokenRef = useRef<string | null>(null);
  // Refreshes spent on the current mismatch, reset when it resolves. Two,
  // because one races the backend: ExecuteRoleChangeCommandUseCase writes the
  // user doc BEFORE the claim, so the doc snapshot can trigger our refresh and
  // the new token can be minted before the claim write commits. One attempt
  // would then declare a perfectly recoverable session unfixable.
  const claimRefreshAttemptsRef = useRef(0);
  const claimRefreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error>();

  const onboardingCompleted = profile?.onboardingCompleted ?? false;

  const profileUnsubRef = useRef<FirestoreUnsubscribe | null>(null);
  const realUserUnsubRef = useRef<FirestoreUnsubscribe | null>(null);
  const wasImpersonatingRef = useRef(false);
  const impersonationTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  const clearProfileSubscriptions = useCallback(() => {
    profileUnsubRef.current?.();
    profileUnsubRef.current = null;
    realUserUnsubRef.current?.();
    realUserUnsubRef.current = null;
    if (impersonationTimeoutRef.current) {
      clearTimeout(impersonationTimeoutRef.current);
      impersonationTimeoutRef.current = null;
    }
    if (claimRefreshTimeoutRef.current) {
      clearTimeout(claimRefreshTimeoutRef.current);
      claimRefreshTimeoutRef.current = null;
    }
  }, []);

  const resetState = useCallback(() => {
    setProfile(null);
    setRealUser(null);
    setRole(null);
    setBusinessUnitId(null);
    setHouseId(null);
    setRealRole(null);
    setRealBusinessUnitId(null);
    setRealHouseId(null);
    setActingUid(null);
    setIsImpersonating(false);
    setImpersonationActive(false);
    setClaimsStale(false);
    refreshedFromTokenRef.current = null;
    claimRefreshAttemptsRef.current = 0;
    clearProfileSubscriptions();
  }, [clearProfileSubscriptions]);

  useEffect(() => {
    /**
     * Deactivation reached a session that is already open (Jira NAV-130).
     *
     * Sign-in refuses a deactivated account, but an open tab never signs in
     * again — it renews its token in the background. The backend revokes the
     * refresh tokens, so no new token is minted; this is the other half, which
     * stops the app acting on the token it already holds until that lapses.
     * Once per session: the subscriptions fire again while signing out.
     */
    let endingSession = false;
    const endDeactivatedSession = () => {
      if (endingSession) return;
      endingSession = true;
      toast.warning(
        t("login:session.deactivated", {
          defaultValue: "Your account has been deactivated.",
        }),
      );
      resetState();
      void firebaseSignOut(auth)
        .catch(logError)
        .finally(() => {
          endingSession = false;
        });
    };

    const handler: NextOrObserver<User> = async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        resetState();
        setProfileResolved(true);
        setLoading(false);
        return;
      }

      setUser(firebaseUser);
      // Synchronously, and *before* the await below — this is the whole of the
      // signed-in window that used to flash /no-access.
      //
      // Signing out sets `profileResolved` true, and it stays true across a
      // sign-in until line ~354 clears it, which is on the far side of a token
      // read. Between `setUser` and that line the state reads user≠null,
      // loading=false, profileResolved=true, profile=null — indistinguishable
      // from "the record arrived and named no role", so `roleMissing` fired
      // and the root gate replaced the URL. Every in-page sign-in went through
      // it; only a cold load escaped, because `profileResolved` starts false.
      setProfileResolved(false);
      setProfileReadFailed(false);

      try {
        const tokenResult = await firebaseUser.getIdTokenResult();
        const claims = readClaims(tokenResult);

        const activeImpersonation = isImpersonationActive(
          claims.impersonating,
          signInTimeOf(tokenResult),
        )
          ? claims.impersonating!
          : undefined;
        // A claim this sign-in does not honour is still on the account, so
        // ask for it to be cleared — best effort, and not awaited: the view
        // is already the admin's own, and a failure leaves nothing honoured.
        if (claims.impersonating && !activeImpersonation) {
          void requestImpersonationStop(firebaseUser.uid).catch(logError);
        }
        const wasImpersonating = wasImpersonatingRef.current;
        wasImpersonatingRef.current = !!activeImpersonation;
        setIsImpersonating(!!activeImpersonation);
        // Keep the write choke point in step with the rendered state: the
        // guard is read outside React, at the moment a mutation runs.
        setImpersonationActive(!!activeImpersonation);

        // The acting identity is the impersonation target's while a session is
        // active; the admin's own claims stay available as real* so a caller
        // can tell "what the app should show" from "what the rules allow".
        // A target without a BU carries "" — normalize it to null so the
        // BU-scoped hooks see the absence they already handle.
        setRole(
          activeImpersonation
            ? (normalizeRole(activeImpersonation.role) ?? null)
            : (claims.role ?? null),
        );
        setBusinessUnitId(
          activeImpersonation
            ? (activeImpersonation.business_unit_id || null)
            : (claims.business_unit_id ?? null),
        );
        // The third claim, on the same rule as the unit: `""` is the unit
        // itself, normalised to null so the house-scoped hooks see the absence
        // they already handle.
        setHouseId(
          activeImpersonation
            ? (activeImpersonation.house_id || null)
            : (claims.house_id || null),
        );
        setRealRole(claims.role ?? null);
        setRealBusinessUnitId(claims.business_unit_id ?? null);
        setRealHouseId(claims.house_id || null);
        setActingUid(activeImpersonation?.uid ?? firebaseUser.uid);

        profileUnsubRef.current?.();
        profileUnsubRef.current = null;
        setProfileResolved(false);
        setProfileReadFailed(false);

        const targetUid = activeImpersonation
          ? activeImpersonation.uid
          : firebaseUser.uid;

        profileUnsubRef.current = onSnapshot(
          doc(db, "users", targetUid),
          (snapshot: DocumentSnapshot<DocumentData>) => {
            const nextProfile = snapshotToProfile(snapshot);
            // The signed-in person's own record saying they were deactivated
            // ends the session here and now (Jira NAV-130). While
            // impersonating this is the *target's* record, so the admin's own
            // is watched below instead.
            if (!activeImpersonation && nextProfile?.status === "INACTIVE") {
              endDeactivatedSession();
              return;
            }
            setProfile(nextProfile);

            // The ID token is cached for up to an hour, so claims minted or
            // changed after it was issued (a new BU admin, a moved user) stay
            // invisible to this client — while the security rules read the
            // *token*. The visible result is a BU-scoped page that renders
            // nothing or errors, because the query the UI builds from stale
            // claims is one the rules refuse. The profile doc is the live
            // truth, so a disagreement means our token is behind: refresh it
            // once, which re-enters this handler with the real claims.
            //
            // When the refresh does NOT reconcile them, the session cannot
            // heal itself and has to say so — otherwise the whole
            // failure is a BU admin looking at an empty requests page, because
            // the rules deny a query built from the stale claim and a denied
            // query reads as "no results". `business_unit_id` is minted only at
            // sign-in, by the before_user_signed_in blocking function, and only
            // as a session claim; we have seen a token where it never arrived
            // and refreshing did not produce it. Which layer wins when a
            // session claim and a persisted custom claim disagree is not
            // something we have established, so this branch is driven by the
            // observed outcome rather than by a rule about precedence: a
            // refresh happened, the token still disagrees, so stop guessing and
            // offer the one thing that certainly re-mints claims from the doc.
            const profileBu = nextProfile?.businessUnitId;
            const profileRole = normalizeRole(nextProfile?.role);
            // Compared against `claims.*` — the raw token — and NOT against the
            // acting `role`/`businessUnitId` impersonation state. Those
            // are the impersonation target's while a session is active, so
            // comparing them to a doc would be comparing two things that are
            // *meant* to differ. The raw claims are what the security rules read,
            // which is the whole point of the check. (`realRole` /
            // `realBusinessUnitId` hold the same values, but reading the claims
            // directly keeps this independent of that plumbing.)
            //
            // The BU is compared symmetrically, normalising absent and "" to
            // the same thing. Guarding on `!!profileBu` instead made a doc with
            // no BU incapable of ever being stale, so a token carrying a BU the
            // doc has since lost read as agreement. `""` vs absent stays
            // benign — a shape difference between two ways of saying "none".
            const buStale =
              nextProfile !== null &&
              (profileBu ?? "") !== (claims.business_unit_id ?? "");
            // Role stays guarded on the doc stating one: an absent `role` is a
            // malformed doc, not a claim mismatch, and treating it as stale
            // would raise a banner that signing in again cannot clear.
            const roleStale = !!profileRole && profileRole !== claims.role;
            // **The house is compared symmetrically too, and for a sharper
            // reason than the unit.** It is minted by the same blocking
            // function, in the same dict, from the same document, so it has the
            // same non-arrival mode — but the two fail in opposite directions.
            // A missing `business_unit_id` *denies*: `isBuAdminOf('')` is
            // refused by its own `buId != ''` guard, which is the empty admin
            // portal this whole check was written to explain. A missing
            // `house_id` **grants**: `callerHouseId()` answers `''`, and `''`
            // is exactly the value that means "an admin of the unit itself".
            //
            // So an admin assigned to a house whose token has not caught up
            // keeps every unit-level write the assignment was meant to take
            // away, reads every house's members and requests, and sees a UI
            // that agrees with the token rather than the doc — with nothing
            // raised, because the unit and the role both still match. Nobody
            // is escalated past what they held a moment earlier, which is why
            // this is not a blocker; a narrowing that has silently not taken
            // effect, with the machinery built to notice it looking past it, is
            // worse than one that fails loudly.
            //
            // The reverse direction is already safe — a token carrying
            // `rowohlt` against a doc that lost it fails closed — so only the
            // absent case needs this, and symmetric normalising is what covers
            // it without a second rule.
            const houseStale =
              nextProfile !== null &&
              (nextProfile.houseId ?? "") !== (claims.house_id ?? "");
            const stale = buStale || roleStale || houseStale;
            // Impersonation deliberately runs on claims the target's doc will
            // not match, so it is not evidence of anything and must also clear
            // a flag raised before it started — telling someone to sign in
            // again mid-impersonation is worse than saying nothing.
            if (activeImpersonation || !stale) {
              setClaimsStale(false);
              // A resolved mismatch returns the refresh budget, so a *later*
              // change (a second role change in one session) gets its own
              // attempt instead of going straight to the banner.
              if (!stale) {
                refreshedFromTokenRef.current = null;
                claimRefreshAttemptsRef.current = 0;
              }
            } else if (
              refreshedFromTokenRef.current === null ||
              (refreshedFromTokenRef.current !== tokenResult.token &&
                claimRefreshAttemptsRef.current < CLAIM_REFRESH_ATTEMPTS)
            ) {
              // Either no attempt yet, or one landed on a new token that still
              // disagrees and we have an attempt left. The second is delayed:
              // the backend writes the user doc before the claim, so the very
              // snapshot that woke us can precede the claim write, and an
              // immediate retry would just re-read the same token.
              const first = refreshedFromTokenRef.current === null;
              refreshedFromTokenRef.current = tokenResult.token;
              claimRefreshAttemptsRef.current += 1;
              const refresh = () => firebaseUser.getIdToken(true).catch(logError);
              if (first) {
                void refresh();
              } else {
                if (claimRefreshTimeoutRef.current) {
                  clearTimeout(claimRefreshTimeoutRef.current);
                }
                claimRefreshTimeoutRef.current = setTimeout(
                  refresh,
                  CLAIM_REFRESH_RETRY_MS,
                );
              }
            } else if (refreshedFromTokenRef.current !== tokenResult.token) {
              // Attempts spent and a new token still disagrees.
              setClaimsStale(true);
            }
            // Remaining case: still on the token we asked to replace, so the
            // refresh is in flight — say nothing until it lands.
            // Initial auth resolution includes the first profile snapshot:
            // onboardingCompleted must be real (not the ?? false fallback)
            // before the router mounts, or the onboarding redirect fires
            // for already-onboarded users on every full page load.
            setProfileResolved(true);
            setProfileReadFailed(false);
            setLoading(false);
          },
          (err) => {
            logError(err);
            setProfile(null);
            setProfileResolved(true);
            // A read that failed has said nothing about whether this account
            // has a role, so `roleMissing` must not decide on the strength of
            // the empty profile it leaves behind — it falls back to waiting.
            setProfileReadFailed(true);
            setLoading(false);
          },
        );

        if (activeImpersonation) {
          realUserUnsubRef.current?.();
          realUserUnsubRef.current = onSnapshot(
            doc(db, "users", firebaseUser.uid),
            (snapshot: DocumentSnapshot<DocumentData>) => {
              const real = snapshotToProfile(snapshot);
              if (real?.status === "INACTIVE") {
                endDeactivatedSession();
                return;
              }
              setRealUser(real);
            },
            (err) => {
              logError(err);
              setRealUser(null);
            },
          );

          if (impersonationTimeoutRef.current) {
            clearTimeout(impersonationTimeoutRef.current);
          }
          const msUntilExpiry =
            activeImpersonation.expires_at * 1000 - Date.now();
          impersonationTimeoutRef.current = setTimeout(() => {
            toast.warning(
              t("auth:impersonation.expired", {
                defaultValue: "Impersonation session has ended.",
              }),
            );
            firebaseUser.getIdToken(true).catch(logError);
          }, Math.max(msUntilExpiry, 0));
        } else if (wasImpersonating) {
          setRealUser(null);
        }
      } catch (err) {
        logError(err);
        setError(err as Error);
        // The profile subscription (which normally resolves loading) may not
        // have been set up when claims reading fails — resolve here instead.
        //
        // `profileResolved` for the same reason: the subscription is what
        // normally sets it, and without it a session that already has a `role`
        // from a previous token — an hourly refresh that failed offline, say —
        // leaves it false for ever. The root gate waits on that signal before
        // resuming an interrupted errand, so the reader would sit on the page
        // they opened rather than being sent on. Saying "the read is over" is
        // true here; what it found is carried by `error`, and `roleMissing`
        // still needs a *resolved and empty* profile, which this is not.
        setProfileResolved(true);
        // **And that it found nothing, which is the other half.** This path can
        // resolve the read without the profile subscription having been set up
        // at all — a throw after `setRole` on a cold load, so the reader has a
        // role and no document. Every consumer that refuses to decide on an
        // absence reads this flag, and one that saw "resolved" with the flag
        // still false would take `onboardingCompleted`'s `?? false` fallback
        // for an answer: `shouldIntroduce` would send an established reader
        // into the walkthrough, and `roleMissing` would call a role missing
        // that was never looked for.
        setProfileReadFailed(true);
        setLoading(false);
      }
    };

    let unsubscribe: Unsubscribe | undefined;
    try {
      unsubscribe = onIdTokenChanged(auth, handler);
    } catch (err) {
      logError(err);
      setLoading(false);
    }

    return () => {
      unsubscribe?.();
      clearProfileSubscriptions();
    };
  }, [clearProfileSubscriptions, resetState]);

  // The clock behind `roleMissing`, running only while a role could still
  // arrive. Reset by any change to the inputs, so a role that lands — or a
  // sign-out — cancels it rather than firing into a session that has moved on.
  useEffect(() => {
    if (!user || loading || role) {
      setRoleGraceElapsed(false);
      return;
    }
    const timer = setTimeout(() => setRoleGraceElapsed(true), ROLE_GRACE_MS);
    return () => clearTimeout(timer);
  }, [user, loading, role]);

  // Signed in, the token names no role, and either the person's own record
  // names none either (nothing is coming) or the wait for the claim to catch
  // up is over. Never during impersonation: `role` is the *target's* there, so
  // a target without one would strand the admin on a page whose only offer is
  // signing out, when what they need is to end the session.
  //
  // The immediate branch rests on the record having *answered*, and answering
  // means a document came back. Three ways it might not have, all of which
  // leave the same empty profile behind and none of which is evidence:
  //
  //  * the read failed — a denied query read as an empty list, the mistake
  //    rather than a shortcut;
  //  * the read has not been made yet — `profileResolved` is stale from the
  //    signed-out state until the handler clears it, which is why it is now
  //    cleared synchronously above as well;
  //  * the document does not exist — a deleted or never-created record, where
  //    `profile` is null and there is genuinely nothing to read a role from.
  //
  // `profile !== null` is what separates all three from the case this branch
  // is for: the record arrived and named no role. The third still reaches
  // /no-access, by the grace window rather than at once, which is the right
  // way round — nobody's account should be judged on a read that produced
  // nothing.
  const roleMissing =
    !!user &&
    !loading &&
    role === null &&
    !isImpersonating &&
    ((profileResolved &&
      !profileReadFailed &&
      profile !== null &&
      !normalizeRole(profile.role)) ||
      roleGraceElapsed);

  // A failed sign-in is reported once, here, as a fault — unless it was a
  // refusal the app decided on (an address nobody allowlisted, a deactivated
  // account). Those are shown by the sign-in page itself, in words that say
  // what to do next; adding "Oops! Something went wrong" on top is what made a
  // working refusal read as a crash, and reporting it to the error backend
  // buries real faults under people who simply need to be added.
  useEffect(() => {
    if (error) {
      if (!signInRefusalOf(error)) {
        logError(error);
        toast.error(
          t("login:error.loginFailed", { defaultValue: "Login failed" }),
        );
      }
      setError(undefined);
    }
  }, [error]);

  const signInWithGoogle = useCallback(async () => {
    try {
      return await signInWithPopup(auth, GOOGLE_PROVIDER);
    } catch (e) {
      setError(e as Error);
      throw e;
    }
  }, []);

  const signOut = useCallback(async () => {
    // Signing out ends an impersonation too (Jira NAV-130). The next sign-in
    // would not honour the claim anyway (`isImpersonationActive`), but asking
    // now clears it from the account and writes the stop to the audit trail
    // under the sign-out it belongs to. Best effort, and **bounded**: a write
    // offline never settles, and neither a failure nor a wait may keep anybody
    // signed in.
    const uid = auth.currentUser?.uid;
    if (isImpersonating && uid) {
      await Promise.race([
        requestImpersonationStop(uid).catch(logError),
        new Promise((resolve) => setTimeout(resolve, SIGN_OUT_STOP_WAIT_MS)),
      ]);
    }
    clearProfileSubscriptions();
    resetState();
    await firebaseSignOut(auth);
  }, [clearProfileSubscriptions, resetState, isImpersonating]);

  const value = useMemo<IAuthContext>(
    () => ({
      user,
      profile,
      role,
      businessUnitId,
      houseId,
      actingUid,
      isImpersonating,
      realRole,
      realBusinessUnitId,
      realHouseId,
      impersonatedUser: isImpersonating ? profile : null,
      realUser,
      onboardingCompleted,
      profileResolved,
      profileReadFailed,
      claimsStale,
      roleMissing,
      loading,
      error,
      isLoggedIn: !!user,
      signInWithGoogle,
      signOut,
    }),
    [
      user,
      profile,
      role,
      businessUnitId,
      houseId,
      actingUid,
      isImpersonating,
      realRole,
      realBusinessUnitId,
      realHouseId,
      realUser,
      onboardingCompleted,
      profileResolved,
      profileReadFailed,
      claimsStale,
      roleMissing,
      loading,
      error,
      signInWithGoogle,
      signOut,
    ],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
};
